import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminGetUsers, adminGetPages, adminGetSettings, adminUpdateSettings, adminExtractFromImage, adminCreateProfileFromExtraction, adminEnrichFromPdf, adminGetWaitlist, adminDeletePage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Users, FileText, Settings, Plus, Eye, Loader2, ArrowLeft, ExternalLink, Upload, Check, X, Image, Code, Sparkles, Clock, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';

// Parse any investor JSON format - extract ALL fields except attribution and source
const parseInvestorJson = (json) => {
  const data = typeof json === 'string' ? JSON.parse(json) : json;
  
  // Remove excluded fields
  const excluded = ['attribution', 'source'];
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([key]) => !excluded.includes(key))
  );
  
  // Helper to find a value by possible key names
  const findValue = (obj, keys) => {
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
    }
    return null;
  };
  
  // Helper to flatten nested objects into readable strings
  const flattenValue = (val, prefix = '') => {
    if (val === null || val === undefined) return [];
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      return [{ key: prefix, value: String(val) }];
    }
    if (Array.isArray(val)) {
      return [{ key: prefix, value: val.join(', ') }];
    }
    if (typeof val === 'object') {
      const results = [];
      for (const [k, v] of Object.entries(val)) {
        const newPrefix = prefix ? `${prefix} ${k}` : k;
        results.push(...flattenValue(v, newPrefix));
      }
      return results;
    }
    return [];
  };
  
  // Extract basic info with flexible key matching
  const name = findValue(cleanData, ['name', 'full_name', 'fullName']) || '';
  const email = findValue(cleanData, ['email', 'contact_email']) || 
                findValue(cleanData.contact || {}, ['email']) || '';
  const title = findValue(cleanData, ['title', 'role', 'position', 'job_title']) || '';
  const company = findValue(cleanData, ['firm', 'company', 'organization', 'current_company']) || '';
  const location = findValue(cleanData, ['location', 'city', 'region', 'based_in']) || '';
  const advice = findValue(cleanData, ['advice_to_founders', 'advice', 'founder_advice', 'tip', 'wisdom']) || '';
  
  // Build tags from any array fields or focus areas
  const tags = [];
  const tagSources = ['focus', 'interests', 'sectors', 'industries', 'expertise', 'specialties', 'areas'];
  for (const [key, val] of Object.entries(cleanData)) {
    if (Array.isArray(val) && val.every(v => typeof v === 'string')) {
      if (tagSources.some(t => key.toLowerCase().includes(t))) {
        tags.push(...val);
      }
    }
  }
  // Also check nested objects like investment_profile.focus
  for (const [key, val] of Object.entries(cleanData)) {
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      for (const [k, v] of Object.entries(val)) {
        if (Array.isArray(v) && v.every(x => typeof x === 'string')) {
          if (tagSources.some(t => k.toLowerCase().includes(t))) {
            tags.push(...v);
          }
        }
        // Add stage/type as tag
        if (['stage', 'type', 'category'].includes(k.toLowerCase()) && typeof v === 'string') {
          tags.unshift(v);
        }
      }
    }
  }
  
  // Build stats from any numeric or key-value pairs in nested objects
  const stats = [];
  const processStats = (obj, parentKey = '') => {
    if (!obj || typeof obj !== 'object') return;
    for (const [key, val] of Object.entries(obj)) {
      const label = key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').toLowerCase().trim();
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        // Handle objects like check_size: { min_usd: 500000, max_usd: 3500000 }
        if (val.min !== undefined && val.max !== undefined) {
          stats.push({ label, value: `${val.min}-${val.max}${val.min_usd ? '' : '%'}` });
        } else if (val.min_usd !== undefined && val.max_usd !== undefined) {
          const formatMoney = (n) => n >= 1000000 ? `$${n/1000000}M` : `$${n/1000}K`;
          stats.push({ label, value: `${formatMoney(val.min_usd)}-${formatMoney(val.max_usd)}` });
        } else {
          processStats(val, key);
        }
      } else if (typeof val === 'string' && !['stage', 'leads', 'focus'].some(x => key.includes(x))) {
        if (parentKey && !tags.includes(val)) {
          stats.push({ label, value: val.toLowerCase() });
        }
      } else if (typeof val === 'number') {
        stats.push({ label, value: String(val) });
      }
    }
  };
  
  // Look for investment_profile or similar nested objects for stats
  for (const [key, val] of Object.entries(cleanData)) {
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      if (['investment_profile', 'investment', 'profile', 'details', 'info'].some(k => key.toLowerCase().includes(k))) {
        processStats(val, key);
      }
    }
  }
  
  // Build content likes from content_preferences or similar
  const content_likes = { books: [], podcasts: [], newsletters: [] };
  const contentSources = ['content_preferences', 'content', 'preferences', 'favorites', 'recommendations'];
  for (const [key, val] of Object.entries(cleanData)) {
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      if (contentSources.some(c => key.toLowerCase().includes(c))) {
        for (const [k, v] of Object.entries(val)) {
          if (typeof v === 'string') {
            if (k.toLowerCase().includes('book')) content_likes.books.push(v);
            else if (k.toLowerCase().includes('podcast')) content_likes.podcasts.push(v);
            else if (k.toLowerCase().includes('newsletter')) content_likes.newsletters.push(v);
          } else if (Array.isArray(v)) {
            if (k.toLowerCase().includes('book')) content_likes.books.push(...v);
            else if (k.toLowerCase().includes('podcast')) content_likes.podcasts.push(...v);
            else if (k.toLowerCase().includes('newsletter')) content_likes.newsletters.push(...v);
          }
        }
      }
    }
  }
  
  // Extract badge from leads or similar
  const badge = findValue(cleanData, ['leads', 'badge', 'tagline', 'headline']) ||
                findValue(cleanData.investment_profile || {}, ['leads', 'stage']) || '';
  
  // Store ALL original data for reference (excluding attribution/source)
  const allExtractedFields = flattenValue(cleanData);
  
  return {
    name,
    current_role: title,
    current_company: company,
    location,
    email,
    badge: typeof badge === 'string' ? badge : '',
    tags: [...new Set(tags)].slice(0, 6), // Dedupe and limit
    stats: stats.slice(0, 3), // Limit to 3 stats
    advice,
    content_likes,
    subtitle: '',
    // Include all raw data for the backend to process
    raw_data: cleanData,
    all_fields: allExtractedFields
  };
};

export default function AdminPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const enrichPdfInputRef = useRef(null);
  
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [settings, setSettings] = useState({ openai_api_key: '', resend_api_key: '' });
  const [loading, setLoading] = useState(true);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // State for create profile flow
  const [createMethod, setCreateMethod] = useState('image'); // 'image' or 'json'
  const [createStep, setCreateStep] = useState('upload'); // 'upload', 'extracting', 'review', 'email', 'enriching', 'enrich-review'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [profileEmail, setProfileEmail] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  
  // Enrichment state
  const [enrichmentData, setEnrichmentData] = useState(null);
  const [selectedEnrichments, setSelectedEnrichments] = useState({});
  
  // Sorting state for pages table
  const [sortColumn, setSortColumn] = useState('published_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [usersData, pagesData, settingsData, waitlistData] = await Promise.all([
        adminGetUsers(),
        adminGetPages(),
        adminGetSettings(),
        adminGetWaitlist()
      ]);
      setUsers(usersData.users || []);
      setPages(pagesData.pages || []);
      setSettings(settingsData || {});
      setWaitlist(waitlistData.waitlist || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (key, value) => {
    setSaving(true);
    try {
      await adminUpdateSettings({ [key]: value });
      await loadData();
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    
    setUploadedFile(file);
    setCreateStep('extracting');
    
    try {
      const result = await adminExtractFromImage(file);
      setExtractedData(result.extracted);
      
      // Check if email was found
      if (result.extracted?.email) {
        setProfileEmail(result.extracted.email);
        setCreateStep('review');
      } else {
        setCreateStep('email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract from image');
      resetCreateFlow();
    }
  };

  const handleCreateProfile = async () => {
    if (!profileEmail) {
      toast.error('Please enter an email');
      return;
    }

    setSaving(true);
    try {
      const result = await adminCreateProfileFromExtraction(profileEmail, extractedData);
      toast.success(`Profile created! Slug: ${result.slug}`);
      resetCreateFlow();
      setShowCreateProfile(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBlankProfile = async () => {
    if (!profileEmail || !profileEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setSaving(true);
    try {
      // Create blank profile with just the email - minimal data
      const blankData = {
        name: '',
        title: '',
        company: '',
        email: profileEmail
      };
      const result = await adminCreateProfileFromExtraction(profileEmail, blankData);
      toast.success(`Blank profile created! Slug: ${result.slug}`);
      resetCreateFlow();
      setShowCreateProfile(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const handleJsonParse = () => {
    try {
      const parsed = parseInvestorJson(jsonInput);
      setExtractedData(parsed);
      
      // Check if email was found
      if (parsed?.email) {
        setProfileEmail(parsed.email);
        setCreateStep('review');
      } else {
        setCreateStep('email');
      }
    } catch (error) {
      toast.error('Invalid JSON format. Please check your input.');
    }
  };

  const handleEnrichPdfSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    
    setCreateStep('enriching');
    
    try {
      const result = await adminEnrichFromPdf(file);
      setEnrichmentData(result.enrichment);
      
      // Pre-select all enrichments by default
      const selections = {};
      if (result.enrichment.journey?.length) selections.journey = true;
      if (result.enrichment.subtitle) selections.subtitle = true;
      if (result.enrichment.suggested_badge) selections.badge = true;
      if (result.enrichment.suggested_tags?.length) selections.tags = true;
      if (result.enrichment.key_achievements?.length) selections.achievements = true;
      if (result.enrichment.education?.length) selections.education = true;
      if (result.enrichment.skills?.length) selections.skills = true;
      setSelectedEnrichments(selections);
      
      setCreateStep('enrich-review');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract from PDF');
      setCreateStep('review');
    }
  };

  const applyEnrichments = () => {
    if (!enrichmentData) return;
    
    const updated = { ...extractedData };
    
    // Apply selected enrichments
    if (selectedEnrichments.journey && enrichmentData.journey?.length) {
      updated.experience = enrichmentData.journey;
    }
    if (selectedEnrichments.subtitle && enrichmentData.subtitle) {
      updated.subtitle = enrichmentData.subtitle;
    }
    if (selectedEnrichments.badge && enrichmentData.suggested_badge) {
      updated.badge = enrichmentData.suggested_badge;
    }
    if (selectedEnrichments.tags && enrichmentData.suggested_tags?.length) {
      // Merge tags, avoiding duplicates
      const existingTags = updated.tags || [];
      const newTags = enrichmentData.suggested_tags.filter(t => !existingTags.includes(t));
      updated.tags = [...existingTags, ...newTags].slice(0, 8);
    }
    if (selectedEnrichments.achievements && enrichmentData.key_achievements?.length) {
      updated.key_achievements = enrichmentData.key_achievements;
    }
    if (selectedEnrichments.education && enrichmentData.education?.length) {
      updated.education = enrichmentData.education;
    }
    if (selectedEnrichments.skills && enrichmentData.skills?.length) {
      updated.skills = enrichmentData.skills;
    }
    
    // Extract contact info from PDF (LinkedIn URL, location, etc.)
    if (enrichmentData.contact) {
      const contact = enrichmentData.contact;
      
      // Set LinkedIn URL from PDF (priority over vcdata)
      if (contact.linkedin_url) {
        let linkedinUrl = contact.linkedin_url;
        // Ensure it has https://
        if (!linkedinUrl.startsWith('http')) {
          linkedinUrl = 'https://' + linkedinUrl;
        }
        updated.social_links = updated.social_links || {};
        updated.social_links.linkedin = linkedinUrl;
      }
      
      // Set location from PDF if available
      if (contact.location && !updated.location) {
        updated.location = contact.location;
      }
      
      // Set website from PDF if available
      if (contact.website) {
        updated.social_links = updated.social_links || {};
        updated.social_links.website = contact.website;
      }
    }
    
    // Store any additional data
    updated.enriched_data = {
      certifications: enrichmentData.certifications,
      languages: enrichmentData.languages,
      volunteer: enrichmentData.volunteer,
      publications: enrichmentData.publications
    };
    
    setExtractedData(updated);
    setEnrichmentData(null);
    setCreateStep('review');
    toast.success('Enrichment applied!');
  };

  const resetCreateFlow = () => {
    setCreateStep('upload');
    setCreateMethod('image');
    setUploadedFile(null);
    setExtractedData(null);
    setProfileEmail('');
    setJsonInput('');
    setEnrichmentData(null);
    setSelectedEnrichments({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (enrichPdfInputRef.current) {
      enrichPdfInputRef.current.value = '';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A1918]" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E4E0]">
        <div className="container-app flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-heading text-2xl font-medium text-[#1A1918]">
              brief
            </Link>
            <span className="text-[#E6E4E0]">/</span>
            <span className="text-[#666460]">Admin</span>
          </div>
          <Link to="/editor">
            <Button variant="ghost" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Editor
            </Button>
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="container-app py-8">
        <Tabs defaultValue="pages" className="space-y-6">
          <TabsList className="bg-white border border-[#E6E4E0]">
            <TabsTrigger value="pages" className="flex items-center gap-2" data-testid="tab-pages">
              <FileText className="w-4 h-4" />
              Pages
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="flex items-center gap-2" data-testid="tab-waitlist">
              <Clock className="w-4 h-4" />
              Waitlist
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-medium text-[#1A1918]">
                Users ({users.length})
              </h2>
            </div>

            <div className="bg-white rounded-2xl border border-[#E6E4E0] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...users]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {u.is_admin ? (
                          <Badge variant="default">Admin</Badge>
                        ) : (
                          <Badge variant="secondary">User</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-[#666460]">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-medium text-[#1A1918]">
                Pages ({pages.length})
              </h2>
              <Button 
                className="btn-primary flex items-center gap-2"
                onClick={() => setShowCreateProfile(true)}
                data-testid="create-profile-btn"
              >
                <Plus className="w-4 h-4" />
                Create Profile
              </Button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E6E4E0] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 select-none"
                      onClick={() => {
                        if (sortColumn === 'magic_link_count') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn('magic_link_count');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Links Sent
                        {sortColumn === 'magic_link_count' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 select-none"
                      onClick={() => {
                        if (sortColumn === 'owner_email') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn('owner_email');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Owner
                        {sortColumn === 'owner_email' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 select-none"
                      onClick={() => {
                        if (sortColumn === 'is_published') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn('is_published');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortColumn === 'is_published' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Accepting Pitches</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 select-none"
                      onClick={() => {
                        if (sortColumn === 'visit_count') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn('visit_count');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Views
                        {sortColumn === 'visit_count' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 select-none"
                      onClick={() => {
                        if (sortColumn === 'last_login') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn('last_login');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Last Logged In
                        {sortColumn === 'last_login' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50 select-none"
                      onClick={() => {
                        if (sortColumn === 'published_at') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortColumn('published_at');
                          setSortDirection('desc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Published
                        {sortColumn === 'published_at' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...pages]
                    .sort((a, b) => {
                      let aVal, bVal;
                      
                      switch (sortColumn) {
                        case 'magic_link_count':
                          aVal = a.magic_link_count || 0;
                          bVal = b.magic_link_count || 0;
                          break;
                        case 'owner_email':
                          aVal = a.owner_email || '';
                          bVal = b.owner_email || '';
                          break;
                        case 'is_published':
                          aVal = a.is_published ? 1 : 0;
                          bVal = b.is_published ? 1 : 0;
                          break;
                        case 'visit_count':
                          aVal = a.visit_count || 0;
                          bVal = b.visit_count || 0;
                          break;
                        case 'last_login':
                          aVal = a.last_login ? new Date(a.last_login) : new Date(0);
                          bVal = b.last_login ? new Date(b.last_login) : new Date(0);
                          break;
                        case 'published_at':
                        default:
                          aVal = a.published_at ? new Date(a.published_at) : new Date(a.created_at || 0);
                          bVal = b.published_at ? new Date(b.published_at) : new Date(b.created_at || 0);
                          break;
                      }
                      
                      if (sortDirection === 'asc') {
                        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
                      } else {
                        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
                      }
                    })
                    .map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="flex items-center gap-1 text-[#666460]">
                          <Mail className="w-3 h-3" />
                          <span>{p.magic_link_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>{p.owner_email}</TableCell>
                      <TableCell>/p/{p.slug}</TableCell>
                      <TableCell>
                        {p.is_published ? (
                          <Badge className="bg-green-100 text-green-800">Published</Badge>
                        ) : (
                          <Badge variant="secondary">Draft</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(p.apps || []).find(a => a.type === 'pitch')?.enabled ? (
                          <span className="text-green-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-[#9CA3AF]">No</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-[#666460]">
                          <Eye className="w-3 h-3" />
                          <span>{p.visit_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#666460] text-sm">
                        {p.last_login ? (
                          <span title={new Date(p.last_login).toISOString()}>
                            {new Date(p.last_login).toLocaleString('en-US', { 
                              timeZone: 'America/Los_Angeles',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })} PST
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-[#666460]">
                        {p.published_at ? new Date(p.published_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Link
                            to={`/admin/edit/${p.id}`}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                          >
                            <FileText className="w-4 h-4" />
                            Edit
                          </Link>
                          <a
                            href={`/p/${p.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-[#666460] hover:text-[#1A1918]"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </a>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Delete profile "${p.slug}"? This cannot be undone.`)) {
                                try {
                                  await adminDeletePage(p.id);
                                  toast.success(`Profile "${p.slug}" deleted`);
                                  setPages(pages.filter(page => page.id !== p.id));
                                } catch (err) {
                                  toast.error('Failed to delete profile');
                                }
                              }
                            }}
                            className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-medium text-[#1A1918]">
                Waitlist ({waitlist.length})
              </h2>
            </div>

            <div className="bg-white rounded-2xl border border-[#E6E4E0] overflow-hidden">
              {waitlist.length === 0 ? (
                <div className="p-12 text-center">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No waitlist signups yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Website</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {waitlist.map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {entry.email}
                        </TableCell>
                        <TableCell>
                          {entry.website ? (
                            <a 
                              href={entry.website.startsWith('http') ? entry.website : `https://${entry.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm"
                            >
                              {entry.website}
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.status === 'pending' ? 'secondary' : 'default'}>
                            {entry.status || 'pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <h2 className="font-heading text-2xl font-medium text-[#1A1918]">
              API Settings
            </h2>

            <div className="max-w-xl space-y-6">
              <div className="bg-white rounded-2xl border border-[#E6E4E0] p-6 space-y-4">
                <div>
                  <Label htmlFor="openai">OpenAI API Key</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="openai"
                      type="password"
                      placeholder={settings.openai_api_key || 'Enter OpenAI API key'}
                      className="flex-1"
                      data-testid="openai-key-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdateSettings('openai_api_key', e.target.value);
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        const input = document.getElementById('openai');
                        if (input.value) {
                          handleUpdateSettings('openai_api_key', input.value);
                        }
                      }}
                      disabled={saving}
                      data-testid="save-openai-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                  <p className="text-xs text-[#666460] mt-1">
                    Used for PDF processing and content generation
                  </p>
                </div>

                <div>
                  <Label htmlFor="resend">Resend API Key</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="resend"
                      type="password"
                      placeholder={settings.resend_api_key || 'Enter Resend API key'}
                      className="flex-1"
                      data-testid="resend-key-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdateSettings('resend_api_key', e.target.value);
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        const input = document.getElementById('resend');
                        if (input.value) {
                          handleUpdateSettings('resend_api_key', input.value);
                        }
                      }}
                      disabled={saving}
                      data-testid="save-resend-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                  <p className="text-xs text-[#666460] mt-1">
                    Used for sending magic link emails
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Profile Modal */}
      <Dialog open={showCreateProfile} onOpenChange={(open) => { 
        setShowCreateProfile(open); 
        if (!open) resetCreateFlow();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Create Profile</DialogTitle>
            <DialogDescription>
              Upload an image or paste JSON to extract profile information.
            </DialogDescription>
          </DialogHeader>
          
          {/* Step 1: Choose Method */}
          {createStep === 'upload' && (
            <div className="space-y-4 mt-4">
              {/* Method Toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setCreateMethod('image')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    createMethod === 'image' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Image className="w-4 h-4" />
                  Upload Image
                </button>
                <button
                  onClick={() => setCreateMethod('json')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    createMethod === 'json' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Code className="w-4 h-4" />
                  Paste JSON
                </button>
              </div>

              {/* Image Upload */}
              {createMethod === 'image' && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-700">Click to upload an image</p>
                    <p className="text-xs text-gray-500 mt-1">JPEG, PNG, or WebP (profile cards, screenshots)</p>
                  </div>
                </>
              )}

              {/* JSON Input */}
              {createMethod === 'json' && (
                <>
                  <Textarea
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    placeholder={`Paste investor JSON here...\n\n{\n  "name": "...",\n  "firm": "...",\n  "title": "...",\n  ...etc\n}`}
                    className="min-h-[200px] font-mono text-xs"
                  />
                  <Button 
                    className="w-full btn-primary" 
                    onClick={handleJsonParse}
                    disabled={!jsonInput.trim()}
                  >
                    Parse JSON
                  </Button>
                </>
              )}
              
              {/* Skip / Create Blank Profile */}
              <div className="border-t pt-4 mt-4">
                <button
                  onClick={() => setCreateStep('blank')}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-2"
                >
                  Skip → Create blank profile
                </button>
              </div>
            </div>
          )}
          
          {/* Step: Blank Profile - Email Required */}
          {createStep === 'blank' && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  Enter an email address to create a blank profile. The user can fill in their details later.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={resetCreateFlow}>
                  Back
                </Button>
                <Button 
                  className="btn-primary" 
                  onClick={handleCreateBlankProfile}
                  disabled={saving || !profileEmail || !profileEmail.includes('@')}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Blank Profile
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 2: Extracting */}
          {createStep === 'extracting' && (
            <div className="py-12 text-center">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-gray-400 mb-4" />
              <p className="text-sm font-medium text-gray-700">Extracting profile information...</p>
              <p className="text-xs text-gray-500 mt-1">This may take a few seconds</p>
            </div>
          )}
          
          {/* Step 3: Email Required */}
          {createStep === 'email' && (
            <div className="space-y-4 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Email not found.</strong> Please enter the email address for this profile.
                </p>
              </div>
              <div>
                <Label htmlFor="profile-email">Email Address</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={resetCreateFlow}>
                  Back
                </Button>
                <Button 
                  className="btn-primary" 
                  onClick={() => setCreateStep('review')}
                  disabled={!profileEmail}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 4: Review */}
          {createStep === 'review' && extractedData && (
            <div className="space-y-4 mt-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-800">
                  Profile information extracted successfully!
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
                {extractedData.name && (
                  <div>
                    <span className="text-xs text-gray-500">Name</span>
                    <p className="text-sm font-medium">{extractedData.name}</p>
                  </div>
                )}
                {(extractedData.current_role || extractedData.current_company) && (
                  <div>
                    <span className="text-xs text-gray-500">Role</span>
                    <p className="text-sm font-medium">
                      {extractedData.current_role}{extractedData.current_company && ` at ${extractedData.current_company}`}
                    </p>
                  </div>
                )}
                {extractedData.location && (
                  <div>
                    <span className="text-xs text-gray-500">Location</span>
                    <p className="text-sm font-medium">{extractedData.location}</p>
                  </div>
                )}
                {extractedData.badge && (
                  <div>
                    <span className="text-xs text-gray-500">Badge</span>
                    <p className="text-sm font-medium">{extractedData.badge}</p>
                  </div>
                )}
                {extractedData.tags && extractedData.tags.length > 0 && (
                  <div>
                    <span className="text-xs text-gray-500">Tags</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {extractedData.tags.map((tag, i) => (
                        <span key={i} className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {extractedData.subtitle && (
                  <div>
                    <span className="text-xs text-gray-500">Bio</span>
                    <p className="text-sm">{extractedData.subtitle}</p>
                  </div>
                )}
                {extractedData.stats && extractedData.stats.length > 0 && (
                  <div>
                    <span className="text-xs text-gray-500">Stats</span>
                    <div className="flex gap-3 mt-1">
                      {extractedData.stats.map((stat, i) => (
                        <div key={i} className="text-center">
                          <p className="text-sm font-bold">{stat.value}</p>
                          <p className="text-xs text-gray-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {extractedData.advice && (
                  <div>
                    <span className="text-xs text-gray-500">Advice for Founders</span>
                    <p className="text-sm italic">&ldquo;{extractedData.advice}&rdquo;</p>
                  </div>
                )}
                {extractedData.content_likes && (extractedData.content_likes.books?.length > 0 || extractedData.content_likes.podcasts?.length > 0 || extractedData.content_likes.newsletters?.length > 0) && (
                  <div>
                    <span className="text-xs text-gray-500">Content Preferences</span>
                    <div className="mt-1 space-y-1">
                      {extractedData.content_likes.books?.map((b, i) => (
                        <p key={`book-${i}`} className="text-sm">📖 {b}</p>
                      ))}
                      {extractedData.content_likes.podcasts?.map((p, i) => (
                        <p key={`podcast-${i}`} className="text-sm">🎙️ {p}</p>
                      ))}
                      {extractedData.content_likes.newsletters?.map((n, i) => (
                        <p key={`newsletter-${i}`} className="text-sm">📬 {n}</p>
                      ))}
                    </div>
                  </div>
                )}
                {/* Show all other extracted fields */}
                {extractedData.all_fields && extractedData.all_fields.length > 0 && (
                  <div className="border-t pt-3 mt-3">
                    <span className="text-xs text-gray-500 font-medium">All Extracted Fields</span>
                    <div className="mt-2 space-y-1 text-xs">
                      {extractedData.all_fields.map((field, i) => (
                        <div key={i} className="flex">
                          <span className="text-gray-400 w-32 flex-shrink-0 truncate">{field.key}:</span>
                          <span className="text-gray-700 truncate">{field.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Enrich with LinkedIn PDF */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <input
                  type="file"
                  ref={enrichPdfInputRef}
                  accept="application/pdf"
                  onChange={handleEnrichPdfSelect}
                  className="hidden"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-800">Enrich with LinkedIn PDF</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => enrichPdfInputRef.current?.click()}
                    className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Upload PDF
                  </Button>
                </div>
                <p className="text-xs text-indigo-600 mt-1">Add journey, experience, and more details from a LinkedIn export</p>
              </div>
              
              <div>
                <Label htmlFor="confirm-email">Email Address</Label>
                <Input
                  id="confirm-email"
                  type="email"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">This will be used for the user to sign in and edit their profile.</p>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={resetCreateFlow}>
                  Start Over
                </Button>
                <Button 
                  className="btn-primary" 
                  onClick={handleCreateProfile}
                  disabled={saving || !profileEmail}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Profile
                </Button>
              </div>
            </div>
          )}
          
          {/* Step: Enriching */}
          {createStep === 'enriching' && (
            <div className="py-12 text-center">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-indigo-500 mb-4" />
              <p className="text-sm font-medium text-gray-700">Extracting enrichment data from LinkedIn PDF...</p>
              <p className="text-xs text-gray-500 mt-1">This may take a few seconds</p>
            </div>
          )}
          
          {/* Step: Enrich Review */}
          {createStep === 'enrich-review' && enrichmentData && (
            <div className="space-y-4 mt-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <p className="text-sm text-indigo-800">
                  Select enrichments to apply to the profile
                </p>
              </div>
              
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {/* Journey/Experience */}
                {enrichmentData.journey && enrichmentData.journey.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.journey ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, journey: !s.journey }))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        🗺️ Journey ({enrichmentData.journey.length} positions)
                        {selectedEnrichments.journey && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {enrichmentData.journey.slice(0, 3).map((j, i) => (
                        <div key={i} className="text-xs">
                          <span className="mr-1">{j.icon || '💼'}</span>
                          <span className="font-medium">{j.company}</span>
                          <span className="text-gray-500"> · {j.role}</span>
                          {j.duration && <span className="text-gray-400 ml-1">({j.duration})</span>}
                        </div>
                      ))}
                      {enrichmentData.journey.length > 3 && (
                        <p className="text-xs text-gray-400">+{enrichmentData.journey.length - 3} more...</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Subtitle/Description */}
                {enrichmentData.subtitle && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.subtitle ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, subtitle: !s.subtitle }))}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium flex items-center gap-2">
                        📝 Short Description
                        {selectedEnrichments.subtitle && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{enrichmentData.subtitle}</p>
                  </div>
                )}
                
                {/* Badge */}
                {enrichmentData.suggested_badge && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.badge ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, badge: !s.badge }))}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium flex items-center gap-2">
                        ✦ Suggested Badge
                        {selectedEnrichments.badge && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{enrichmentData.suggested_badge}</span>
                    </div>
                  </div>
                )}
                
                {/* Tags */}
                {enrichmentData.suggested_tags && enrichmentData.suggested_tags.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.tags ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, tags: !s.tags }))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        🏷️ Suggested Tags
                        {selectedEnrichments.tags && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {enrichmentData.suggested_tags.map((tag, i) => (
                        <span key={i} className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Key Achievements */}
                {enrichmentData.key_achievements && enrichmentData.key_achievements.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.achievements ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, achievements: !s.achievements }))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        🏆 Key Achievements
                        {selectedEnrichments.achievements && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                    </div>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {enrichmentData.key_achievements.slice(0, 3).map((a, i) => (
                        <li key={i}>• {a}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Education */}
                {enrichmentData.education && enrichmentData.education.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.education ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, education: !s.education }))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        🎓 Education
                        {selectedEnrichments.education && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {enrichmentData.education.map((e, i) => (
                        <p key={i} className="text-xs text-gray-600">{e.institution} - {e.degree}</p>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Skills */}
                {enrichmentData.skills && enrichmentData.skills.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedEnrichments.skills ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setSelectedEnrichments(s => ({ ...s, skills: !s.skills }))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        💡 Skills
                        {selectedEnrichments.skills && <Check className="w-4 h-4 text-green-600" />}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{enrichmentData.skills.slice(0, 8).join(', ')}</p>
                  </div>
                )}
                
                {/* Additional Data (non-selectable, just info) */}
                {(enrichmentData.certifications?.length > 0 || enrichmentData.languages?.length > 0 || enrichmentData.volunteer?.length > 0) && (
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <span className="text-sm font-medium text-gray-500">Additional Data (will be stored)</span>
                    <div className="mt-2 text-xs text-gray-500 space-y-1">
                      {enrichmentData.certifications?.length > 0 && (
                        <p>📜 {enrichmentData.certifications.length} certifications</p>
                      )}
                      {enrichmentData.languages?.length > 0 && (
                        <p>🌍 Languages: {enrichmentData.languages.join(', ')}</p>
                      )}
                      {enrichmentData.volunteer?.length > 0 && (
                        <p>🤝 {enrichmentData.volunteer.length} volunteer activities</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => { setEnrichmentData(null); setCreateStep('review'); }}>
                  Skip Enrichment
                </Button>
                <Button 
                  className="btn-primary" 
                  onClick={applyEnrichments}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Apply Selected ({Object.values(selectedEnrichments).filter(Boolean).length})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
